import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { searchMedicamentosDirectly } from '../../services/medicamentosSearchDirect';

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  outline: none;
  transition: all ${props => props.theme.transitions.fast};
  
  &:focus {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid ${props => props.theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  list-style: none;
  margin: 0;
  padding: 0;
`;

const SuggestionItem = styled.li`
  padding: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid ${props => props.theme.colors.surface};
  
  &:hover, &.selected {
    background-color: ${props => props.theme.colors.surface};
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  .nombre {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
  }
  
  .detalles {
    font-size: 14px;
    color: ${props => props.theme.colors.text.secondary};
    margin-top: 4px;
  }
  
  .codigo {
    font-size: 12px;
    color: ${props => props.theme.colors.primary};
  }
`;

const LoadingMessage = styled.div`
  padding: 12px;
  text-align: center;
  color: ${props => props.theme.colors.text.secondary};
`;

export const AutocompleteInput = ({ 
  value, 
  onChange, 
  onSelect, 
  placeholder = "Escribe el nombre del medicamento...",
  ...inputProps 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Buscar medicamentos por nombre
  const searchMedicamentos = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    
    try {
      console.log('Buscando:', searchText);
      const results = await searchMedicamentosDirectly(searchText);
      console.log('Resultados encontrados:', results.length);
      setSuggestions(results);
    } catch (error) {
      console.error('Error buscando medicamentos:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounce la búsqueda
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchMedicamentos(value);
    }, 300);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [value]);

  // Manejar clicks fuera del componente
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    setShowSuggestions(true);
    setSelectedIndex(-1);
  };

  const handleSelectSuggestion = (medicamento) => {
    onSelect(medicamento);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  return (
    <Container ref={containerRef}>
      <Input
        {...inputProps}
        value={value}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      
      {showSuggestions && value.length >= 2 && (
        <SuggestionsList>
          {loading ? (
            <LoadingMessage>Buscando...</LoadingMessage>
          ) : suggestions.length > 0 ? (
            suggestions.map((medicamento, index) => (
              <SuggestionItem
                key={medicamento.id}
                className={index === selectedIndex ? 'selected' : ''}
                onClick={() => handleSelectSuggestion(medicamento)}
              >
                <div className="nombre">{medicamento.nombre}</div>
                <div className="detalles">
                  {medicamento.presentacion && `${medicamento.presentacion} - `}
                  {medicamento.proveedor}
                </div>
                <div className="codigo">Código: {medicamento.codigoBarras || medicamento.id}</div>
              </SuggestionItem>
            ))
          ) : (
            <LoadingMessage>No se encontraron medicamentos</LoadingMessage>
          )}
        </SuggestionsList>
      )}
    </Container>
  );
};